import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { toaster } from "components/common/Toast"
import { fetchEmailDomain, registerEmailDomain, updateFromAddress, removeEmailDomain, refreshDomainStatus } from "store/email-domain"
import { VERIFICATION_STATUS } from "./StatusBadge"

export const useEmailDomain = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    domain,
    from_address,
    from_name,
    verification_status,
    dkim_records,
    spf_record,
    loading,
    updateLoading,
    refreshLoading,
    removeLoading
  } = useSelector((state) => state.emailDomain)

  const [domainInput, setDomainInput] = useState("")
  const [fromAddressInput, setFromAddressInput] = useState("")
  const [fromNameInput, setFromNameInput] = useState("")
  const [removeModalOpen, setRemoveModalOpen] = useState(false)

  useEffect(() => {
    dispatch(fetchEmailDomain())
  }, [dispatch])

  useEffect(() => {
    setFromAddressInput(from_address ?? "")
    setFromNameInput(from_name ?? "")
  }, [from_address, from_name])

  const handleRegisterDomain = () => {
    if (!domainInput.trim()) return
    dispatch(registerEmailDomain(domainInput.trim(), fromAddressInput.trim(), fromNameInput.trim())).then(() => {
      toaster.success(t("customEmail.toast.domain_registered"))
      setDomainInput("")
    })
  }

  const handleSaveFromAddress = () => {
    if (!fromAddressInput.trim()) return
    const escapedDomain = domain.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`)
    const emailRegex = new RegExp(`^[^@]+@${escapedDomain}$`)
    if (!emailRegex.test(fromAddressInput.trim())) {
      toaster.error(t("customEmail.toast.invalid_from_address", { domain }))
      return
    }
    dispatch(updateFromAddress(fromAddressInput.trim(), fromNameInput.trim())).then((action) => {
      toaster.success(action?.payload?.message || t("customEmail.toast.from_address_saved"))
    })
  }

  const handleRefreshStatus = () => {
    dispatch(refreshDomainStatus()).then((response) => {
      if (response?.ses_domain_status === VERIFICATION_STATUS.pending) {
        toaster.info(t("customEmail.toast.status_still_pending"))
      } else if (response?.ses_domain_status === VERIFICATION_STATUS.verified) {
        toaster.success(t("customEmail.toast.status_verified"))
      } else if (response?.ses_domain_status === VERIFICATION_STATUS.failed) {
        toaster.error(t("customEmail.toast.status_failed"))
      } else {
        toaster.success(t("customEmail.toast.status_refreshed"))
      }
    })
  }

  const handleRemoveDomain = () => {
    dispatch(removeEmailDomain()).then((response) => {
      toaster.success(response?.message || t("customEmail.toast.domain_removed"))
      setRemoveModalOpen(false)
      setFromAddressInput("")
      setFromNameInput("")
    })
  }

  return {
    domain,
    from_address,
    from_name,
    verification_status,
    dkim_records,
    spf_record,
    loading,
    updateLoading,
    refreshLoading,
    removeLoading,
    domainInput,
    setDomainInput,
    fromAddressInput,
    setFromAddressInput,
    fromNameInput,
    setFromNameInput,
    removeModalOpen,
    setRemoveModalOpen,
    handleRegisterDomain,
    handleSaveFromAddress,
    handleRefreshStatus,
    handleRemoveDomain
  }
}
